/* 17_제품_Shipping §3 — delivery_comparison_table 로직 & 데이터 처리.
 * ai-lists IS-05 전치: 배송 유형=열 · 항목=행, columns[] depth=2 → tbody rowspan 그룹.
 */

export interface TableColumn {
  key: string;
  label: string;
  label_i18n?: string;
  sub_label?: string;
  depth?: 1 | 2;
}

export interface TableRow {
  rule_id?: string;
  rule_name?: string;
  price?: string;
  earliest_delivery?: string;
  shipment_start?: string;
  [key: string]: unknown;
}

export interface ColumnVisibility {
  earliest_delivery?: boolean;
  shipment_start?: boolean;
}

export interface ShippingTable {
  layout?: "table" | "single" | "empty";
  columns?: TableColumn[];
  rows?: TableRow[];
  disclaimer?: string;
  message?: string;
  purchase_offer_type?: string;
  calendar_mode?: string;
  column_visibility?: ColumnVisibility;
  shipment_start_date?: string | null;
  shipment_start_iso?: string | null;
}

export interface ShippingData {
  ok?: boolean;
  trade_up?: boolean;
  pbp_cta?: boolean;
  table?: ShippingTable;
  tables?: Array<{ ok?: boolean; sku?: string; table?: ShippingTable }>;
  metadata?: { [key: string]: unknown };
}

export type SimpleRowSpec = {
  kind: "simple";
  label: string;
  columnKey: string;
  alignRight?: boolean;
  multiLine?: boolean;
};

export type GroupRowSpec = {
  kind: "group";
  label: string;
  subs: Array<{ label: string; columnKey: string }>;
};

export type RowSpec = SimpleRowSpec | GroupRowSpec;

export const EMPTY_CELL = "-";

export function resolveTables(data: ShippingData): Array<{ sku?: string; table: ShippingTable }> {
  if (Array.isArray(data.tables) && data.tables.length) {
    return data.tables
      .filter((t) => t.ok && t.table)
      .map((t) => ({ sku: t.sku, table: t.table as ShippingTable }));
  }
  if (data.table) return [{ table: data.table }];
  return [];
}

export function splitEarliestDates(value: string): string[] {
  const text = value.trim();
  if (!text || text === EMPTY_CELL) return [EMPTY_CELL];
  return text.split(/,\s*/).filter(Boolean);
}

/** OMS 셀 "Wall-mounting (£49.99)" → "£49.99" (행 라벨에 서비스명이 있음). */
export function extractServiceFee(cell: string): string {
  const text = cell.trim();
  if (!text || text === EMPTY_CELL) return EMPTY_CELL;
  const match = text.match(/\(([^)]+)\)$/);
  return match ? match[1].trim() : text;
}

function isServiceColumnKey(key: string): boolean {
  return key.startsWith("install:") || key.startsWith("haulaway:") || key.startsWith("timeslot:");
}

/** columns[] → tbody 행 spec (rule_name 제외, timeslot 포함). */
export function buildRowSpecs(columns: TableColumn[]): RowSpec[] {
  const specs: RowSpec[] = [];
  const dataColumns = columns.filter((col) => col.key !== "rule_name");

  for (let i = 0; i < dataColumns.length; ) {
    const col = dataColumns[i];
    if (col.depth === 2) {
      const groupLabel = col.label;
      const subs: GroupRowSpec["subs"] = [];
      while (
        i < dataColumns.length
        && dataColumns[i].depth === 2
        && dataColumns[i].label === groupLabel
      ) {
        subs.push({
          label: dataColumns[i].sub_label || dataColumns[i].label,
          columnKey: dataColumns[i].key,
        });
        i += 1;
      }
      if (subs.length === 1) {
        specs.push({
          kind: "simple",
          label: groupLabel,
          columnKey: subs[0].columnKey,
          alignRight: true,
        });
      } else {
        specs.push({ kind: "group", label: groupLabel, subs });
      }
      continue;
    }

    specs.push({
      kind: "simple",
      label: col.label,
      columnKey: col.key,
      alignRight: col.key === "price",
      multiLine: col.key === "earliest_delivery",
    });
    i += 1;
  }

  return specs;
}

export function cellValue(row: TableRow, spec: SimpleRowSpec): string | string[] {
  const raw = String(row[spec.columnKey] ?? EMPTY_CELL);
  if (spec.multiLine) return splitEarliestDates(raw);
  if (isServiceColumnKey(spec.columnKey)) return extractServiceFee(raw);
  return raw;
}
